import { useCallback } from 'react';
import { Tooltip } from '@forethought-technologies/forethought-elements';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  editQuickFeedback,
  quickFeedbackClicked,
  selectQuickFeedbackConfig,
  selectQuickFeedbackSteps,
} from 'reducers/workflows';
import { icons, useStyles } from './icons';
import QuickFeedbackButton from './QuickFeedbackButton';

export default function QuickFeedbackButtons({
  isButton,
  send,
  size,
  stepIndex,
}: {
  isButton: boolean;
  send: (isUseful?: boolean) => void;
  size: number;
  stepIndex: number;
}) {
  const dispatch = useAppDispatch();

  const quickFeedbackConfig = useAppSelector(selectQuickFeedbackConfig);
  const quickFeedbackSteps = useAppSelector(selectQuickFeedbackSteps);

  const { badStyles, goodStyles } = useStyles();

  const quickFeedbackStep = quickFeedbackSteps[stepIndex];

  const isGoodQuickFeedbackSent =
    quickFeedbackStep?.isSent && quickFeedbackStep?.isUseful;
  const isBadQuickFeedbackSent =
    quickFeedbackStep?.isSent && !quickFeedbackStep?.isUseful;

  const handleQuickFeedbackClick = useCallback(
    (isUseful: boolean) => {
      // Clicked the same button again:
      if (
        (isUseful && isGoodQuickFeedbackSent) ||
        (!isUseful && isBadQuickFeedbackSent)
      ) {
        dispatch(editQuickFeedback(stepIndex));

        return;
      }

      dispatch(quickFeedbackClicked({ isUseful, stepIndex }));

      // If additional feedback is disabled, send request right away:
      if (
        isUseful
          ? !quickFeedbackConfig?.should_request_feedback_for_high_ratings
          : !quickFeedbackConfig?.should_request_feedback_for_low_ratings
      ) {
        send(isUseful);
      }
    },
    [
      dispatch,
      send,
      isBadQuickFeedbackSent,
      isGoodQuickFeedbackSent,
      quickFeedbackConfig?.should_request_feedback_for_high_ratings,
      quickFeedbackConfig?.should_request_feedback_for_low_ratings,
      stepIndex,
    ],
  );

  const handleGoodQuickFeedbackClick = useCallback(
    () => handleQuickFeedbackClick(true),
    [handleQuickFeedbackClick],
  );

  const handleBadQuickFeedbackClick = useCallback(
    () => handleQuickFeedbackClick(false),
    [handleQuickFeedbackClick],
  );

  if (!quickFeedbackConfig) {
    return null;
  }

  const { BadIcon, GoodIcon } = icons[quickFeedbackConfig.style];

  return (
    <>
      <Tooltip tooltipContent={quickFeedbackConfig.hover_label_good}>
        <QuickFeedbackButton
          aria-label={quickFeedbackConfig.hover_label_good}
          component='button'
          hoverIcon={
            <GoodIcon size={size} strokeWidth='1.5px' {...goodStyles} />
          }
          isActive={isGoodQuickFeedbackSent}
          isButton={isButton}
          onClick={handleGoodQuickFeedbackClick}
          size={size}
          type='button'
        >
          <GoodIcon
            size={size}
            strokeWidth='1.5px'
            {...(isGoodQuickFeedbackSent ? goodStyles : {})}
          />
        </QuickFeedbackButton>
      </Tooltip>
      <Tooltip tooltipContent={quickFeedbackConfig.hover_label_bad}>
        <QuickFeedbackButton
          aria-label={quickFeedbackConfig.hover_label_bad}
          component='button'
          hoverIcon={<BadIcon size={size} strokeWidth='1.5px' {...badStyles} />}
          isActive={isBadQuickFeedbackSent}
          isButton={isButton}
          onClick={handleBadQuickFeedbackClick}
          size={size}
          type='button'
        >
          <BadIcon
            size={size}
            strokeWidth='1.5px'
            {...(isBadQuickFeedbackSent ? badStyles : {})}
          />
        </QuickFeedbackButton>
      </Tooltip>
    </>
  );
}
